import React, { useState, useEffect } from 'react'
import { useParams, Link } from 'react-router-dom'
import supabase from '../services/supabase'
import Newsletter from '../components/Newsletter'
import Faq from '../components/Faq'
import SchemaMarkup from '../components/SchemaMarkup'
import CanonicalUrl from '../components/CanonicalUrl'
import StatusCode from '../components/StatusCode'

const Cnae2009DetailsPage = () => {
  const { cod_cnae_2009, slug_cnae_2009 } = useParams()
  const [cnaeDetails, setCnaeDetails] = useState(null)
  const [sectionTitle, setSectionTitle] = useState('')
  const [associatedIaeCodes, setAssociatedIaeCodes] = useState([])
  const [relatedCnaeCodes, setRelatedCnaeCodes] = useState([])
  const [correspondingCnae2025Codes, setCorrespondingCnae2025Codes] = useState(
    []
  )
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    const fetchCnaeDetails = async () => {
      try {
        setIsLoading(true)
        setError(null)

        let cnaeData = null
        let cnaeError = null

        // First try to fetch by slug
        const slugResult = await supabase.rpc('get_cnae_2009_details_by_slug', {
          input_slug: slug_cnae_2009,
        })

        if (slugResult.data && slugResult.data.length > 0) {
          cnaeData = slugResult.data
          cnaeError = null
        } else {
          // If no results by slug, try to fetch by code

          const codeResult = await supabase.rpc(
            'get_cnae_2009_details_by_code',
            {
              cnae_code_2009: cod_cnae_2009,
            }
          )

          cnaeData = codeResult.data
          cnaeError = codeResult.error
        }

        if (cnaeError) {
          console.error('Error fetching CNAE 2009 details:', cnaeError)
        }

        if (!cnaeData || cnaeData.length === 0) {
          throw new Error('No se encontró el código CNAE 2009')
        }

        setCnaeDetails(cnaeData[0])

        // Fetch section title
        if (cnaeData[0].cod_integro_cnae_2009) {
          const { data: sectionData, error: sectionError } = await supabase.rpc(
            'get_cnae_2009_section_title_by_code',
            { codigo_integro_cnae: cnaeData[0].cod_integro_cnae_2009 }
          )

          if (!sectionError && sectionData && sectionData.length > 0) {
            setSectionTitle(sectionData[0].titulo_seccion)
          } else {
            console.error('Error fetching section title:', sectionError)
          }
        }

        // Process related CNAE codes
        if (
          cnaeData[0].relaciones_cnae_2009 &&
          cnaeData[0].relaciones_cnae_2009.length > 0
        ) {
          // Get slug for each related CNAE code
          const relatedCodesWithSlugs = await Promise.all(
            cnaeData[0].relaciones_cnae_2009.map(async (code) => {
              // Get CNAE slug by code
              const { data: slugData } = await supabase.rpc(
                'get_slug_cnae_2009_by_code',
                {
                  input_cnae_code: code,
                }
              )

              return {
                code,
                slug:
                  slugData && slugData.length > 0
                    ? slugData[0].slug_cnae_2009
                    : code,
              }
            })
          )

          setRelatedCnaeCodes(relatedCodesWithSlugs)
        } else {
          setRelatedCnaeCodes([])
        }

        // Fetch associated IAE codes using the CNAE slug
        const { data: iaeCodesData, error: iaeCodesError } = await supabase.rpc(
          'get_iae_associated_to_cnae_by_slug',
          { cnae_slug: slug_cnae_2009 }
        )

        if (iaeCodesError) {
          console.error('Error fetching associated IAE codes:', iaeCodesError)
          setAssociatedIaeCodes([])
        } else if (iaeCodesData && iaeCodesData.length > 0) {
          // Format the IAE codes with their slugs and titles
          const formattedIaeCodes = iaeCodesData.map((item) => ({
            code: item.codigo_iae_asociado,
            slug: item.slug_iae,
            title: item.titulo_iae,
          }))

          setAssociatedIaeCodes(formattedIaeCodes)
        } else {
          setAssociatedIaeCodes([])
        }

        // Fetch corresponding CNAE 2025 codes
        if (cnaeData[0].cod_cnae_2009) {
          const { data: cnae2025CodesData, error: cnae2025CodesError } =
            await supabase.rpc(
              'get_cnae_2025_corresponding_to_cnae_2009_by_code',
              {
                cnae_code: cnaeData[0].cod_cnae_2009,
              }
            )

          if (cnae2025CodesError) {
            console.error(
              'Error fetching corresponding CNAE 2025 codes:',
              cnae2025CodesError
            )
            setCorrespondingCnae2025Codes([])
          } else if (cnae2025CodesData && cnae2025CodesData.length > 0) {
            // Format the CNAE 2025 codes with their slugs
            const formattedCnae2025Codes = await Promise.all(
              cnae2025CodesData.map(async (item) => {
                // Get CNAE 2025 details by code to get the slug
                const { data: cnae2025Details, error: cnae2025DetailsError } =
                  await supabase.rpc('get_cnae_2025_overview_by_code', {
                    cnae_code: item,
                  })

                if (cnae2025DetailsError) {
                  console.error(
                    'Error fetching CNAE 2025 details:',
                    cnae2025DetailsError
                  )
                  return {
                    code: item,
                    slug: item,
                    title: '',
                  }
                }

                return {
                  code: item,
                  slug:
                    cnae2025Details && cnae2025Details.length > 0
                      ? cnae2025Details[0].slug_cnae
                      : item,
                  title:
                    cnae2025Details && cnae2025Details.length > 0
                      ? cnae2025Details[0].titulo_cnae
                      : '',
                }
              })
            )

            setCorrespondingCnae2025Codes(formattedCnae2025Codes)
          } else {
            setCorrespondingCnae2025Codes([])
          }
        }

        // Search counter is now incremented in SearchBar.js when a code is selected
      } catch (error) {
        console.error('Error fetching CNAE 2009 details:', error)
        setError(
          error.message || 'Error al cargar los detalles del código CNAE 2009'
        )
      } finally {
        setIsLoading(false)
      }
    }

    if (slug_cnae_2009 && cod_cnae_2009) {
      fetchCnaeDetails()
    }
  }, [slug_cnae_2009, cod_cnae_2009])

  // Format description text with proper line breaks
  const formatText = (text) => {
    if (!text) return null

    // Split by line breaks and filter out empty lines
    const paragraphs = text.split('\n').filter((line) => line.trim() !== '')

    return paragraphs.map((paragraph, index) => (
      <p key={index} className='mb-3'>
        {paragraph}
      </p>
    ))
  }

  // Format date to DD-MM-YYYY
  const formatDate = (dateString) => {
    if (!dateString) return ''
    const date = new Date(dateString)
    return `${date.getDate().toString().padStart(2, '0')}-${(
      date.getMonth() + 1
    )
      .toString()
      .padStart(2, '0')}-${date.getFullYear()}`
  }

  return (
    <div className='container'>
      {isLoading ? (
        <div className='loading'>
          <div className='loading-spinner'></div>
        </div>
      ) : error ? (
        <>
          <StatusCode code={404} />
          <div className='error-message'>{error}</div>
        </>
      ) : (
        <>
          {/* Canonical URL */}
          <CanonicalUrl
            url={`https://conversoriaecnae.es/cnae-2009/${cnaeDetails.cod_cnae_2009.replace('.', '')}/${cnaeDetails.slug_cnae_2009}`}
          />

          {/* Dataset Schema */}
          <SchemaMarkup
            type='Dataset'
            data={{
              name: `Código CNAE 2009: ${cnaeDetails.cod_cnae_2009} - ${cnaeDetails.titulo_cnae_2009}`,
              description: cnaeDetails.descripcion_cnae_2009,
              keywords: cnaeDetails.palabras_claves_cnae_2009 || [],
              url: `https://conversoriaecnae.es/cnae-2009/${cnaeDetails.cod_cnae_2009.replace(
                '.',
                ''
              )}/${cnaeDetails.slug_cnae_2009}`,
              identifier: cnaeDetails.cod_cnae_2009,
              creator: 'Conversor IAE CNAE',
              datePublished: cnaeDetails.ultima_actualizacion_cnae_2009,
              dateModified: cnaeDetails.ultima_actualizacion_cnae_2009,
            }}
          />
          <div className='bg-surface rounded-lg p-6 my-6'>
            <div className='mb-4'>
              <span className='bg-cnae2009 text-background text-xs font-bold px-2 py-1 rounded'>
                CNAE 2009
              </span>
            </div>

            <h1 className='text-xl font-bold mb-1'>
              {cnaeDetails.cod_cnae_2009}
            </h1>
            <h2 className='text-xl font-semibold mb-6'>
              {cnaeDetails.titulo_cnae_2009}
            </h2>

            <div className='mb-4'>
              <h3 className='text-primary font-bold mb-1'>Sección</h3>
              <p className='text-text-primary'>{sectionTitle}</p>
            </div>

            <div className='mb-4'>
              <h3 className='text-primary font-bold mb-1'>Código íntegro</h3>
              <p className='text-text-primary'>
                {cnaeDetails.cod_integro_cnae_2009}
              </p>
            </div>

            <div className='mb-4'>
              <h3 className='text-primary font-bold mb-1'>Descripción</h3>
              <div className='text-text-primary'>
                {formatText(cnaeDetails.descripcion_cnae_2009)}
              </div>
            </div>

            {cnaeDetails.notas_cnae_2009 && (
              <div className='mb-4'>
                <h3 className='text-primary font-bold mb-1'>Notas</h3>
                <div className='text-text-primary'>
                  {formatText(cnaeDetails.notas_cnae_2009)}
                </div>
              </div>
            )}

            <div className='mb-4'>
              <h3 className='text-primary font-bold mb-1'>Categoría</h3>
              <p className='text-text-primary'>
                {cnaeDetails.categoria_cnae_2009}
              </p>
            </div>

            <div className='mb-4'>
              <h3 className='text-primary font-bold mb-1'>Nivel</h3>
              <p className='text-text-primary'>{cnaeDetails.nivel_cnae_2009}</p>
            </div>

            {cnaeDetails.palabras_claves_cnae_2009 &&
              cnaeDetails.palabras_claves_cnae_2009.length > 0 && (
                <div className='mb-4'>
                  <h3 className='text-primary font-bold mb-1'>
                    Palabras claves
                  </h3>
                  <div className='flex flex-wrap gap-2'>
                    {cnaeDetails.palabras_claves_cnae_2009.map(
                      (keyword, index) => (
                        <span
                          key={index}
                          className='bg-card px-2 py-1 rounded text-sm'
                        >
                          {keyword}
                        </span>
                      )
                    )}
                  </div>
                </div>
              )}

            <div className='mb-4'>
              <h3 className='text-primary font-bold mb-1'>
                Códigos CNAE 2025 correspondientes
              </h3>
              {correspondingCnae2025Codes.length > 0 ? (
                <div className='flex flex-wrap gap-2'>
                  {correspondingCnae2025Codes.map((code, index) => (
                    <Link
                      key={index}
                      to={`/cnae-2025/${code.code.replace('.', '')}/${
                        code.slug
                      }`}
                      className='text-cnae hover:underline'
                      title={code.title}
                    >
                      {code.code} {code.title}
                    </Link>
                  ))}
                </div>
              ) : (
                <p className='text-text-secondary'>
                  No se encontraron códigos CNAE 2025 correspondientes
                </p>
              )}
            </div>

            <div className='mb-4'>
              <h3 className='text-primary font-bold mb-1'>
                Epígrafes IAE asociados
              </h3>
              {associatedIaeCodes.length > 0 ? (
                <div className='flex flex-wrap gap-2'>
                  {associatedIaeCodes.map((code, index) => (
                    <Link
                      key={index}
                      to={`/iae/${code.code.replace('.', '')}/${code.slug}`}
                      className='text-iae hover:underline'
                    >
                      {code.code} {code.title}
                    </Link>
                  ))}
                </div>
              ) : (
                <p className='text-text-secondary'>
                  No se encontraron códigos IAE asociados
                </p>
              )}
            </div>

            {relatedCnaeCodes.length > 0 && (
              <div className='mb-4'>
                <h3 className='text-primary font-bold mb-1'>
                  Códigos CNAE 2009 relacionados
                </h3>
                <div className='flex flex-wrap gap-2'>
                  {relatedCnaeCodes.map((code, index) => (
                    <Link
                      key={index}
                      to={`/cnae-2009/${code.code.replace('.', '')}/${
                        code.slug
                      }`}
                      className='text-cnae2009 hover:underline'
                    >
                      {code.code}
                    </Link>
                  ))}
                </div>
              </div>
            )}

            <div className='mb-4'>
              <h3 className='text-primary font-bold mb-1'>Fuente</h3>
              <p className='text-text-primary'>
                {cnaeDetails.fuente_cnae_2009}
              </p>
            </div>

            <div className='mb-4'>
              <h3 className='text-primary font-bold mb-1'>
                Última actualización
              </h3>
              <p className='text-text-primary'>
                {formatDate(cnaeDetails.ultima_actualizacion_cnae_2009)}
              </p>
            </div>
          </div>

          {/* Newsletter Section */}
          <div className='mt-12'>
            <Newsletter />
          </div>

          {/* FAQ Section */}
          <Faq
            title='Preguntas Frecuentes sobre CNAE 2009'
            description='Resolviendo tus dudas sobre la Clasificación Nacional de Actividades Económicas 2009'
            faqData={[
              {
                question: '¿Qué es el CNAE 2009?',
                answer:
                  'La Clasificación Nacional de Actividades Económicas (CNAE) es un sistema utilizado en España para clasificar y codificar las actividades económicas realizadas por empresas y autónomos. Esta clasificación facilita la recopilación de datos estadísticos, facilita la gestión fiscal y permite una adecuada categorización de las actividades económicas a nivel nacional.\n\nLa CNAE 2009 fue aprobada mediante el Real Decreto 475/2007, de 13 de abril, publicado en el Boletín Oficial del Estado (BOE) número 102, de 28 de abril de 2007. Esta clasificación entró en vigor el 1 de enero de 2009, sustituyendo a la versión anterior, CNAE-93.',
              },
              {
                question:
                  '¿Cuáles son las principales diferencias entre la CNAE 2009 y la CNAE 2025?',
                answer:
                  'Las principales diferencias entre la CNAE 2009 y la CNAE 2025 son:\n\nMayor número de clases: La CNAE 2025 incluye 664 clases, en comparación con las 629 clases de la CNAE 2009.\nReestructuración de algunas actividades: Se han creado nuevas clases para actividades emergentes, como el comercio electrónico, plataformas digitales, y sectores vinculados con la sostenibilidad y las energías renovables.\nModificaciones en divisiones específicas: Algunas actividades, como las relacionadas con la fabricación de productos y la industria del reciclaje, han sido reagrupadas para reflejar de forma más precisa las tendencias económicas actuales.',
              },
              {
                question:
                  '¿Cómo afecta a las empresas la transición de la CNAE 2009 a la CNAE 2025?',
                answer:
                  'La transición de la CNAE 2009 a la CNAE 2025 implica que las empresas deberán actualizar su código CNAE si su actividad se ve modificada o si los cambios en la estructura del CNAE afectan a su clasificación. La actualización es importante para mantener la precisión en los trámites fiscales, administrativos y estadísticos. Las empresas tendrán hasta el 30 de junio de 2025 para actualizar su código CNAE ante la Seguridad Social.',
              },
              {
                question:
                  '¿Dónde puedo consultar el código CNAE de mi actividad económica?',
                answer:
                  'Para identificar el código CNAE correcto para tu actividad económica:\n\nUtiliza nuestro conversor introduciendo una descripción de tu actividad\nRevisa las sugerencias automáticas que te ofrece la herramienta\nExamina la descripción detallada de cada código para encontrar el que mejor se ajusta\nVerifica que el código seleccionado incluye todas las actividades que realizas\n\nEn caso de duda, puedes consultar con un asesor fiscal o directamente con la Agencia Tributaria.',
              },
              {
                question:
                  '¿Qué plazo tiene mi empresa para actualizar el código CNAE?',
                answer:
                  'Las empresas deben actualizar su código CNAE a más tardar el 30 de junio de 2025. Este plazo se establece para que las empresas puedan adaptar sus registros a la nueva clasificación antes de que se implemente de manera definitiva en los procesos administrativos y fiscales.',
              },
              {
                question:
                  '¿Qué sucede si mi empresa no actualiza su código CNAE?',
                answer:
                  'No actualizar el código CNAE puede llevar a inconsistencias en los registros fiscales y administrativos. Además, podría afectar el cálculo de impuestos, la obtención de beneficios fiscales y la correcta clasificación en estadísticas oficiales. Es fundamental que las empresas mantengan su código CNAE actualizado para cumplir con los requisitos legales y fiscales.',
              },
              {
                question: '¿Cómo puedo convertir un código CNAE 2009 a IAE?',
                answer:
                  'Para convertir un código IAE a CNAE, simplemente utiliza nuestra herramienta de conversión. Ingresa el código IAE en el campo de búsqueda y selecciona el resultado correcto de la lista de sugerencias. Nuestro conversor te proporcionará el código CNAE correspondiente a tu actividad, asegurando que puedas cumplir con los requisitos fiscales y administrativos de manera eficiente.',
              },
              {
                question:
                  '¿Cómo actualizo mi código CNAE 2009 al nuevo CNAE 2025 correspondiente?',
                answer:
                  'La actualización de tu código CNAE al nuevo estándar CNAE 2025 es un proceso necesario para todas las empresas. Debes realizar esta actualización antes del 30 de junio de 2025. Puedes utilizar las tablas de correspondencia entre la CNAE 2025 y la CNAE 2009 proporcionadas por el Instituto Nacional de Estadística (INE) o, simplemente, utilizar nuestro conversor.',
              },
              {
                question:
                  '¿Qué es la Tabla de correspondencia entre CNAE 2009 y CNAE 2025?',
                answer:
                  'Las tablas de correspondencia son herramientas fundamentales para comparar datos estadísticos recopilados y presentados utilizando diferentes clasificaciones, como la CNAE-2025 y CNAE-2009. Su necesidad surge cuando se produce un cambio en la clasificación a lo largo del tiempo o cuando existen diferentes marcos subyacentes que impiden una estrecha relación entre clasificaciones. Las tablas de correspondencia entre diferentes versiones de la misma clasificación describen en detalle los cambios que se han producido en el proceso de revisión.',
              },
            ]}
          />
        </>
      )}
    </div>
  )
}

export default Cnae2009DetailsPage
